import { useCallback, useEffect, useRef, useState } from 'react'

import { asFailure, type Failure } from '../api/request'
import {
  type Task,
  addTask,
  changeTaskDue,
  changeTaskText,
  fetchTasks,
  removeTask,
  tickOffTask,
} from '../api/tasks'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { openedIn, TASKS_AREA } from './tasks/place'
import { OpenTask } from './tasks/OpenTask'
import { TaskCard } from './tasks/TaskCard'

/**
 * Tasks: the open points, as opposed to the things you know.
 *
 * The whole area is built around one sentence from the issue — *much of what I write down during a
 * lecture is not an insight but an open point* — and two decisions follow from it.
 *
 * **Writing one down costs one line and one key.** The field is at the top, it is the first thing on
 * the screen, and Enter is the whole of "save". No dialog, no title, no folder, and a date only if
 * there is one; anything that has to be decided first is the reason the open point ends up nowhere.
 *
 * **The undated stand first.** That is the ordering's whole point rather than a detail of it: a task
 * with a day speaks up on its own as that day comes near, and one without a day never does. Any list
 * sorted by date alone buries exactly the one that most needs looking at.
 *
 * There is no hand-made order here and nothing to drag, which is the largest single thing this area
 * does *not* have compared with the notepad beside it. An arrangement over things that are due is an
 * arrangement that is wrong by the following morning.
 */
export function Tasks() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const opened = openedIn(route.path)
  const openId = opened.taskId
  const announce = useAnnounce()

  const list = useLoad(useCallback((signal: AbortSignal) => fetchTasks(signal), []))
  const [refused, setRefused] = useState<Failure | null>(null)
  const [typed, setTyped] = useState('')
  const field = useRef<HTMLInputElement | null>(null)

  const tasks = list.load.status === 'ready' ? list.load.value : []
  const open = tasks.find((task) => task.id === openId)

  // The order is the service's — one index, one pass — so this only cuts the list into the three
  // parts the screen shows. Nothing is re-sorted here: a second opinion about what "soonest" means
  // is exactly how two of them start to disagree.
  const undated = tasks.filter((task) => task.doneAt === null && task.dueOn === null)
  const dated = tasks.filter((task) => task.doneAt === null && task.dueOn !== null)
  const done = tasks.filter((task) => task.doneAt !== null)

  /*
   * The address can name a task this screen has never heard of — which is what arriving from the
   * search does. Once per id: the answer may genuinely not contain it, and asking for ever would be
   * a request per render.
   */
  const asked = useRef<string | null>(null)
  useEffect(() => {
    if (openId === null || list.load.status !== 'ready' || open !== undefined) {
      return
    }
    if (asked.current !== openId) {
      asked.current = openId
      list.retry()
    }
  }, [openId, open, list])

  // `#/tasks/new` is the cursor in the field, and nothing else: no row is created, and a reload
  // lands where somebody was rather than on a task that never existed.
  useEffect(() => {
    if (opened.writing) {
      field.current?.focus()
    }
  }, [opened.writing])

  const go = (task?: string) => {
    navigate(TASKS_AREA, task === undefined ? [] : [task])
  }

  /** Does something that can be refused, and puts the refusal where it can be read. */
  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  const write = () => {
    const wanted = typed.trim()
    if (wanted === '') {
      return
    }
    // Cleared at once, so the next sentence can be typed straight away rather than after a request.
    setTyped('')
    void attempt(async () => {
      await addTask(wanted)
      list.retry()
      announce('Written down.')
    })
  }

  const tickOff = (task: Task, isDone: boolean) => {
    void attempt(async () => {
      await tickOffTask(task.id, isDone)
      list.retry()
      announce(isDone ? 'Ticked off.' : 'Open again.')
    })
  }

  const changeText = (task: Task, text: string) => {
    void attempt(async () => {
      await changeTaskText(task.id, text)
      list.retry()
      announce('Changed.')
    })
  }

  const changeDue = (task: Task, dueOn: string | null) => {
    void attempt(async () => {
      await changeTaskDue(task.id, dueOn)
      list.retry()
      announce(dueOn === null ? 'No date any more.' : 'Date set.')
    })
  }

  const remove = (task: Task) => {
    void attempt(async () => {
      await removeTask(task.id)
      list.retry()
      if (task.id === openId) {
        go()
      }
      announce('Deleted.')
    })
  }

  const card = (task: Task) => (
    <TaskCard
      /*
        Keyed by what the service last said about this task, not by its id alone.

        A card holds the sentence being typed in it, and that has to be seeded from the task rather
        than kept in step with it on every render — so the way a change arrives is a new card. It
        costs nothing: a save happens when the field is left, so the field it replaces is one nobody
        is typing in.
      */
      key={`${task.id}-${task.updatedAt}`}
      task={task}
      open={task.id === openId}
      onTickOff={(isDone) => {
        tickOff(task, isDone)
      }}
      onChangeText={(text) => {
        changeText(task, text)
      }}
      onChangeDue={(dueOn) => {
        changeDue(task, dueOn)
      }}
      onOpen={() => {
        go(task.id)
      }}
    />
  )

  return (
    <Lanes
      overview={{
        label: 'Tasks',
        content: (
          <div className="tasks">
            <header className="tasks__head">
              <h2 className="tasks__title">Tasks</h2>
            </header>

            {/*
              The field is the way in, and it is the first thing on the screen. A sentence and Enter:
              nothing else is asked for, because everything that is asked for first is a reason not to
              write the open point down at all.
            */}
            <form
              className="tasks__write"
              onSubmit={(event) => {
                event.preventDefault()
                write()
              }}
            >
              <label className="visually-hidden" htmlFor="new-task">
                Write a task
              </label>
              <input
                id="new-task"
                ref={field}
                className="tasks__field"
                value={typed}
                placeholder="Something to do — a sentence is enough."
                onChange={(event) => {
                  setTyped(event.target.value)
                }}
              />
              {/*
                Visible beside the key, because Enter alone would be a shortcut nobody can see — and
                on a phone the keyboard's own return key is not always what somebody reaches for.
              */}
              <Button tone="primary" type="submit" disabled={typed.trim() === ''}>
                Add
              </Button>
            </form>

            {refused !== null && <Failed failure={refused} />}

            {list.load.status === 'waiting' && <Waiting label="Fetching your tasks." />}
            {list.load.status === 'failed' && (
              <Failed
                failure={list.load.failure}
                onRetry={list.retry}
                title="The tasks did not load"
              />
            )}

            {list.load.status === 'ready' && tasks.length === 0 && (
              <Offer
                title="Your open points"
                lead="Something to look up, a question to ask, a script to fetch, a case to solve by Friday. Write it down in the seconds you have — a sentence is enough, and a date only if it has one."
                points={[
                  'Anything without a date stands at the top: nothing else will ever remind you of it.',
                  'Dated ones sort themselves by how much time is left.',
                  'Ticked off stays visible for a month, and nothing ever removes one that is still open.',
                ]}
              />
            )}

            {undated.length > 0 && (
              <section className="tasks__part">
                <h3 className="tasks__part-title">No date</h3>
                <ul className="tasks__list">{undated.map(card)}</ul>
              </section>
            )}

            {dated.length > 0 && (
              <section className="tasks__part">
                <h3 className="tasks__part-title">Coming up</h3>
                <ul className="tasks__list">{dated.map(card)}</ul>
              </section>
            )}

            {done.length > 0 && (
              <section className="tasks__part tasks__part--done">
                <h3 className="tasks__part-title">Done</h3>
                <ul className="tasks__list">{done.map(card)}</ul>
              </section>
            )}
          </div>
        ),
      }}
      detail={
        open === undefined
          ? null
          : {
              label: 'Task',
              content: (
                <OpenTask
                  // The same reason as on the card: the date field is seeded from the task, so a
                  // changed task is a new surface rather than one being written into from outside.
                  key={`${open.id}-${open.updatedAt}`}
                  task={open}
                  onChangeDue={(dueOn) => {
                    changeDue(open, dueOn)
                  }}
                  onTickOff={(isDone) => {
                    tickOff(open, isDone)
                  }}
                  onRemove={() => {
                    remove(open)
                  }}
                  onClose={() => {
                    go()
                  }}
                />
              ),
            }
      }
    />
  )
}
